import { pineScriptIdFromSeriesType, resolveSeriesDatumValue, } from "../../lib/core/seriesValueResolver";
const EMPTY_PINE_SERIES_DATA = { seriesData: {}, signature: "" };
function scalarOf(value) {
    if (typeof value === "number")
        return Number.isFinite(value) ? value : undefined;
    // A multi-plot Pine script produces a keyed object per bar. There is no single
    // "right" band to draw for that, so we take the first declared plot rather than
    // inventing a channel. `computeIndicatorComputationResult` picks the first key the
    // same way, so engine and widget agree on what the line is.
    if (value && typeof value === "object" && !Array.isArray(value)) {
        const first = Object.values(value)[0];
        return typeof first === "number" && Number.isFinite(first) ? first : undefined;
    }
    return undefined;
}
/**
 * Pure. Walks the pane descriptors, keeps only runtime-generated Pine series, and projects
 * each one onto a global-index value array taken from `plotData`.
 *
 * `plotData` must be the widget's React-state array (memoised at `useWidgetData.ts:1283`), NOT
 * `engine.enrichedData`: the latter is behind two hash-guarded push chokepoints
 * (`VNInvestCharts.tsx:593,603` and `useWidgetData.ts:1336`), so reading it here would make
 * this function silently return stale data. See plan M-1.
 */
export function buildPineSeriesData(panes, plotData) {
    if (!panes || panes.length === 0 || !plotData || plotData.length === 0) {
        return EMPTY_PINE_SERIES_DATA;
    }
    const seriesData = {};
    const seenTypes = [];
    for (const pane of panes) {
        if (!pane.series || pane.series.length === 0)
            continue;
        for (const series of pane.series) {
            const scriptId = pineScriptIdFromSeriesType(String(series.type));
            // Unknown / non-Pine types are not this module's business. Anything reaching the
            // engine through `seriesData` must have passed the tier gate first.
            if (!scriptId)
                continue;
            seenTypes.push(String(series.type));
            // Without an id the engine cannot address the series at all (`seriesData[series.id]`),
            // and `paneDescriptorBridge` would synthesise a positional id the producer never saw.
            const id = series.id;
            if (!id)
                continue;
            const values = new Array(plotData.length);
            for (let i = 0; i < plotData.length; i += 1) {
                values[i] = scalarOf(resolveSeriesDatumValue(plotData[i], series));
            }
            const plotOffsetRaw = series.params?.plotOffset;
            const plotOffset = typeof plotOffsetRaw === 'number' && Number.isFinite(plotOffsetRaw)
                ? Math.trunc(plotOffsetRaw)
                : 0;
            if (plotOffset !== 0 && Math.abs(plotOffset) <= 1000) {
                const shifted = new Array(plotData.length);
                for (let i = 0; i < plotData.length; i += 1) {
                    const j = i - plotOffset;
                    shifted[i] = (j >= 0 && j < values.length) ? values[j] : undefined;
                }
                seriesData[id] = shifted;
            }
            else {
                seriesData[id] = values;
            }
        }
    }
    if (seenTypes.length === 0)
        return EMPTY_PINE_SERIES_DATA;
    seenTypes.sort();
    return { seriesData, signature: seenTypes.join("|") };
}
/**
 * Columnar OHLCV view of the enriched datums, which is what the compiler and the Pine worker
 * expect. The single-chart mount site used to pass nothing at all, so the worker validated every
 * script against ZERO bars — a second silent no-op next to the one Stage 1.1/1.2 fixed.
 */
export function toPineCompileBars(bars) {
    const out = { open: [], high: [], low: [], close: [], volume: [] };
    if (!bars)
        return out;
    for (let i = 0; i < bars.length; i += 1) {
        const b = bars[i];
        out.open.push(b?.open ?? 0);
        out.high.push(b?.high ?? 0);
        out.low.push(b?.low ?? 0);
        out.close.push(b?.close ?? 0);
        out.volume.push(b?.volume ?? 0);
    }
    return out;
}
/** Ordered list of Pine series types present, independent of value. Cheap (O(#series)). */
export function pineSeriesSignature(panes) {
    if (!panes || panes.length === 0)
        return "";
    const types = [];
    for (const pane of panes) {
        if (!pane.series)
            continue;
        for (const series of pane.series) {
            if (pineScriptIdFromSeriesType(String(series.type)))
                types.push(String(series.type));
        }
    }
    if (types.length === 0)
        return "";
    types.sort();
    return types.join("|");
}
/**
 * Builds the object handed to `EngineHost` -> `engine.setDataSource(...)`. Pure so that the
 * caller can memoise on its inputs; `DataSourceConfig` is entirely optional
 * (`vn-kline-engine-core/src/types.ts:88-94`), so omitting `seriesData` when there is no Pine
 * script keeps the payload byte-identical to the pre-Pine shape.
 */
export function buildEngineDataSource(identity, pine) {
    const { adapterLabel, activeSymbol, activeTimeframe } = identity;
    const hasPine = Object.keys(pine.seriesData).length > 0;
    return {
        sourceId: `${adapterLabel}:${activeSymbol}:${activeTimeframe}`,
        symbol: activeSymbol,
        timeframe: activeTimeframe,
        adapterLabel,
        ...(hasPine ? { seriesData: pine.seriesData } : {}),
    };
}
